@if (isLoading()) {
  <div class="pt-32">
    <aw-loading-animation />
  </div>
} @else {
  <div class="flex items-start justify-between pt-6 font-medium">
    <div class="flex flex-col gap-1">
      <h1 class="text-3xl" data-testid="section-header-headline">
        {{ cmsTextProvider.corporate.headline }}
      </h1>
      <p class="text-standard-text-secondary text-base font-normal">
        {{ cmsTextProvider.corporate.instructions }}
      </p>
    </div>

    <div class="flex gap-3">
      <aw-button
        id="corporate-design-cancel"
        type="button"
        [style]="'primary-alt'"
        [isDisabled]="!hasUnsavedChanges()"
        [text]="cmsTextProvider.shared.cancel"
        (click)="cancel()" />
      <aw-button
        id="corporate-design-save"
        type="submit"
        [style]="'primary'"
        [isDisabled]="!hasUnsavedChanges() || !isPaletteValid()"
        [text]="cmsTextProvider.shared.save"
        (click)="save()" />
    </div>
  </div>

  <div class="border-standard-divider mt-6 border-b">
    <section
      class="grid items-start gap-6 pb-7 lg:grid-cols-[minmax(12rem,1fr)_minmax(30rem,40rem)_minmax(8rem,1fr)]"
      aria-labelledby="color-palette-heading">
      <h2 id="color-palette-heading" class="px-3 text-xl font-semibold">
        {{ cmsTextProvider.corporate.colorPalette }}
      </h2>

      <div class="min-w-0">
        <p class="text-standard-text-secondary mb-6 text-base leading-6">
          {{ cmsTextProvider.corporate.paletteDialogDescription }}
        </p>

        @if (paletteEntries().length === 0) {
          <div
            class="flex min-h-40 flex-col items-center justify-center text-center"
            data-testid="empty-color-palette">
            <span
              class="material-symbols-rounded mb-3 text-3xl"
              aria-hidden="true">
              add_circle
            </span>
            <p class="text-lg font-medium">
              {{ cmsTextProvider.corporate.emptyPaletteTitle }}
            </p>
            <p class="text-standard-text-secondary mt-2 text-sm">
              {{ cmsTextProvider.corporate.emptyPaletteDescription }}
            </p>
            <button
              type="button"
              class="bg-primary-main text-primary-on mt-3 flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold"
              data-testid="add-first-palette-color"
              (click)="addColor()">
              <span
                class="material-symbols-rounded text-base"
                aria-hidden="true">
                add
              </span>
              {{ cmsTextProvider.corporate.addColor }}
            </button>
          </div>
        } @else {
          <div
            class="border-standard-divider grid grid-cols-[1.25rem_1.75rem_minmax(9rem,1fr)_minmax(9rem,1fr)_3.25rem_2.5rem] items-center gap-3 border-b pb-2 text-sm font-semibold">
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
            <span>{{ cmsTextProvider.corporate.hexValue }}</span>
            <span>{{ cmsTextProvider.corporate.colorName }}</span>
            <span class="text-center">
              {{ cmsTextProvider.corporate.primary }}
            </span>
            <span
              class="group text-primary-main relative flex cursor-help items-center justify-center"
              tabindex="0">
              <span
                class="material-symbols-rounded text-base"
                aria-hidden="true">
                info
              </span>
              <span
                role="tooltip"
                class="pointer-events-none absolute top-[calc(100%+0.5rem)] right-0 z-30 hidden w-72 rounded-lg bg-black p-3 text-sm leading-6 font-normal text-white shadow-xl group-hover:block group-focus:block">
                {{ cmsTextProvider.corporate.primaryColorInfo }}
              </span>
            </span>
          </div>

          <div class="flex flex-col gap-2 pt-2">
            @for (
              color of paletteEntries();
              track color.id;
              let index = $index
            ) {
              <div
                class="grid min-h-9 grid-cols-[1.25rem_1.75rem_minmax(9rem,1fr)_minmax(9rem,1fr)_3.25rem_2.5rem] items-start gap-3"
                [draggable]="index !== 0"
                (dragstart)="startColorDrag(index)"
                (dragover)="$event.preventDefault()"
                (drop)="dropColor(index, $event)">
                <span
                  class="material-symbols-rounded text-base pt-1.5"
                  [ngClass]="index === 0 ? 'cursor-default opacity-30' : 'cursor-grab'"
                  aria-hidden="true">
                  drag_handle
                </span>

                <span
                  class="border-standard-divider block size-6 rounded-md border mt-1.5"
                  [style.background-color]="swatchColor(color.value)"
                  aria-hidden="true"></span>

                <div
                  class="border-standard-divider relative flex h-9 min-w-0 items-center rounded-md border bg-white px-3"
                  [class.border-error-main]="color.value.length !== 6">
                  <input
                    class="min-w-0 flex-1 border-0 bg-transparent text-sm uppercase outline-none focus:ring-0"
                    maxlength="7"
                    [value]="color.value"
                    [attr.aria-label]="cmsTextProvider.corporate.hexValue"
                    (input)="updateColorValue(index, $event)" />
                  <button
                    type="button"
                    class="material-symbols-rounded cursor-pointer text-base"
                    [attr.aria-label]="cmsTextProvider.corporate.pickColor"
                    (click)="
                      activeColorPickerId.set(
                        activeColorPickerId() === color.id ? null : color.id
                      )
                    ">
                    colorize
                  </button>
                  @if (activeColorPickerId() === color.id) {
                    <aw-color-picker
                      [value]="color.value"
                      (valueChange)="updateColorFromPicker(index, $event)"
                      (closed)="activeColorPickerId.set(null)" />
                  }
                </div>
                <div>
                  <input
                    style="width: 100%;"
                    class="focus:border-input-focus h-9 min-w-0 rounded-md border px-3 text-sm outline-none"
                    [value]="color.name"
                    [attr.aria-label]="cmsTextProvider.corporate.colorName"
                    (input)="updateColorName(index, $event)"
                    [ngClass]="{
                      'border-input-error': !validColorName(index, color.name),
                      'border-standard-divider': validColorName(index, color.name)
                    }" />
                    @if(!validColorName(index, color.name)){
                      <div class="text-input-error" >{{ cmsTextProvider.corporate.colorNameInvalid }}</div>
                    }
                </div>

                <button
                  type="button"
                  class="material-symbols-rounded cursor-pointer text-xl pt-1"
                  [class.icon-filled]="color.primary"
                  [class.text-primary-main]="color.primary"
                  [class.text-standard-text-disabled]="!color.primary"
                  [attr.aria-label]="cmsTextProvider.corporate.setPrimary"
                  (click)="confirmNewPrimary(index)">
                  star
                </button>

                <button
                  type="button"
                  class="border-error-main text-error-main hover:bg-error-main/10 flex size-8 cursor-pointer items-center justify-center rounded-md border"
                  [attr.aria-label]="cmsTextProvider.shared.delete"
                  (click)="activeConfirmOverlay.set(index)">
                  <span
                    class="material-symbols-rounded text-xl!"
                    aria-hidden="true">
                    delete
                  </span>
                  @if (activeConfirmOverlay() === index) {
                    <aw-simple-overlay
                      [messageContent]="messageContent" 
                      (closed)="activeConfirmOverlay.set(null)"
                      (accepted)="
                        deleteColor(index);
                        activeConfirmOverlay.set(null);
                      "
                    />
                    <ng-template #messageContent>
                        <span class="text-lg font-medium">
                          {{
                            cmsTextProvider.corporate.deleteColorConfirm
                              | templateString : { colorName: color.name }
                          }}
                        </span>
                    </ng-template>
                  }
                </button>
              </div>
            }
          </div>

          <button
            type="button"
            class="text-primary-main border-primary-main hover:bg-standard-hover-main mt-3 flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm font-semibold"
            data-testid="add-palette-color"
            (click)="addColor()">
            <span class="material-symbols-rounded text-base" aria-hidden="true">
              add
            </span>
            {{ cmsTextProvider.corporate.addColor }}
          </button>
        }
      </div>
      <span aria-hidden="true"></span>
    </section>

    <section
      class="border-standard-divider grid items-start gap-6 border-t py-5 lg:grid-cols-[minmax(12rem,1fr)_minmax(30rem,40rem)_minmax(8rem,1fr)]"
      aria-labelledby="element-shape-heading">
      <h2 id="element-shape-heading" class="px-3 text-xl font-semibold">
        {{ cmsTextProvider.corporate.elementShape }}
      </h2>

      <div class="flex flex-col gap-2">
        <div
          class="border-standard-divider flex h-10 overflow-hidden rounded-md border text-base"
          role="group"
          [attr.aria-label]="cmsTextProvider.corporate.elementShape">
          <button
            type="button"
            class="border-standard-divider hover:bg-standard-hover-main flex flex-1 cursor-pointer items-center justify-center gap-2 border-r"
            [class.bg-standard-selected]="roundingMode() === 'square'"
            [class.font-semibold]="roundingMode() === 'square'"
            (click)="selectBorderRounding('square')">
            @if (roundingMode() === 'square') {
              <span class="material-symbols-rounded text-lg" aria-hidden="true">
                check
              </span>
            }
            {{ cmsTextProvider.corporate.square }}
          </button>
          <button
            type="button"
            class="border-standard-divider hover:bg-standard-hover-main flex flex-1 cursor-pointer items-center justify-center gap-2 border-r"
            [class.bg-standard-selected]="roundingMode() === 'round'"
            [class.font-semibold]="roundingMode() === 'round'"
            (click)="selectBorderRounding('round')">
            @if (roundingMode() === 'round') {
              <span class="material-symbols-rounded text-lg" aria-hidden="true">
                check
              </span>
            }
            {{ cmsTextProvider.corporate.round }}
          </button>
          <button
            type="button"
            class="hover:bg-standard-hover-main flex flex-1 cursor-pointer items-center justify-center gap-2"
            [class.bg-standard-selected]="roundingMode() === 'custom'"
            [class.font-semibold]="roundingMode() === 'custom'"
            (click)="selectBorderRounding('custom')">
            @if (roundingMode() === 'custom') {
              <span class="material-symbols-rounded text-lg" aria-hidden="true">
                check
              </span>
            }
            {{ cmsTextProvider.corporate.custom }}
          </button>
        </div>

        @if (roundingMode() === 'custom') {
          <div
            class="border-standard-divider flex h-9 items-center rounded-md border px-3 text-base">
            <input
              class="min-w-0 flex-1 appearance-none border-0 bg-transparent text-right outline-none focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              type="number"
              [min]="minimumBorderRounding"
              [max]="maximumBorderRounding"
              step="1"
              [value]="customBorderRounding()"
              [attr.aria-label]="cmsTextProvider.corporate.customRounding"
              (input)="updateCustomBorderRounding($event)" />
            <span class="text-standard-text-secondary px-2">px</span>
            <button
              type="button"
              class="hover:bg-standard-hover-main flex size-7 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed disabled:opacity-40"
              [disabled]="customBorderRounding() >= maximumBorderRounding"
              [attr.aria-label]="cmsTextProvider.corporate.increaseRounding"
              (click)="changeCustomBorderRounding(1)">
              <span
                class="material-symbols-rounded text-base"
                aria-hidden="true">
                add
              </span>
            </button>
            <button
              type="button"
              class="hover:bg-standard-hover-main flex size-7 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed disabled:opacity-40"
              [disabled]="customBorderRounding() <= minimumBorderRounding"
              [attr.aria-label]="cmsTextProvider.corporate.decreaseRounding"
              (click)="changeCustomBorderRounding(-1)">
              <span
                class="material-symbols-rounded text-base"
                aria-hidden="true">
                remove
              </span>
            </button>
          </div>
        }

        <p class="text-standard-text-secondary text-sm leading-5">
          {{ cmsTextProvider.corporate.elementShapeHint }}
        </p>
      </div>
      <span aria-hidden="true"></span>
    </section>
  </div>
}

<ng-template #confirmDialog>
  <p class="text-standard-text text-lg" style="white-space: pre-wrap">{{cmsTextProvider.corporate.changePrimaryHint }}</p>
</ng-template>

<ng-template #confirmDialogFooter>
  <div class="flex gap-3 ml-auto">
    <aw-button
      id="primary-hint-cancel"
      type="button"
      [style]="'primary-alt'"
      [text]="cmsTextProvider.shared.cancel"
      (click)="dialogRef?.close()" />
    <aw-button
      id="primary-hint-accept"
      type="button"
      [style]="'primary'"
      [text]="cmsTextProvider.shared.accept"
      (click)="this.setPrimaryColor()" />
  </div>
</ng-template>